// Format-string interpretation. Author-facing spec is a small subset of
// Python's format() mini-language (chosen because it's what the legacy
// briefing taught), with an optional d3-format-style leading sign-spec
// (`+`, `-`, or ` `) so authors can express things like `+.1f` for a
// signed delta. Anything we don't recognise falls back to String(v).

// Sign specifier supported as an optional leading character. Mirrors
// d3-format: `+` always shows a sign, ` ` uses a space for non-negatives,
// `-` is the default (only negatives get a sign).
type SignSpec = "+" | " " | "-";

// Leading sign-spec, capturing group used in the decimal-pattern regexes.
const SIGN = "([+\\- ])?";

const CURRENCY_DECIMALS = new RegExp(`^${SIGN}\\$,\\.(\\d+)f$`);
const COMMA_DECIMALS = new RegExp(`^${SIGN},\\.(\\d+)f$`);
const FIXED_DECIMALS = new RegExp(`^${SIGN}\\.(\\d+)f$`);
const PERCENT_DECIMALS = new RegExp(`^${SIGN}\\.(\\d+)%$`);

// Exact-string formats with optional leading sign-spec, captured.
const EXACT_CURRENCY = /^([+\- ])?\$$/;
const EXACT_PERCENT = /^([+\- ])?%$/;
const EXACT_COMMA = /^([+\- ])?,$/;

// Prepend a sign prefix to an already-formatted number string. Negatives
// already carry "-" from Intl/toFixed, so this only kicks in for
// non-negative values when the author asked for `+` or ` `.
function applySign(formatted: string, sign: SignSpec | undefined, value: number): string {
  if (!sign || sign === "-") return formatted;
  if (value < 0) return formatted;
  return (sign === "+" ? "+" : " ") + formatted;
}

export function formatValue(
  value: number | string | bigint | any,
  format?: string
): string {
  // 1. Catch native BigInts and Arrow Int64 proxy objects
  if (typeof value === "bigint" || (typeof value === "object" && value !== null)) {
    value = Number(value);
  }

  // 2. Catch stringified numbers (ONLY if a format is explicitly requested)
  // This prevents breaking actual string labels like "Pro Shop"
  if (typeof value === "string" && format) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && value.trim() !== "") {
      value = parsed;
    }
  }

  if (typeof value === "string") return value;
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (!format) return String(value);

  let m: RegExpExecArray | null;

  if ((m = EXACT_CURRENCY.exec(format))) {
    return applySign(
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }

  if ((m = EXACT_PERCENT.exec(format))) {
    return applySign(
      new Intl.NumberFormat("en-US", {
        style: "percent",
        maximumFractionDigits: 1,
      }).format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }

  if ((m = EXACT_COMMA.exec(format))) {
    return applySign(
      new Intl.NumberFormat("en-US").format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }

  if ((m = CURRENCY_DECIMALS.exec(format))) {
    const d = parseInt(m[2], 10);
    return applySign(
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }
  if ((m = COMMA_DECIMALS.exec(format))) {
    const d = parseInt(m[2], 10);
    return applySign(
      new Intl.NumberFormat("en-US", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }
  if ((m = FIXED_DECIMALS.exec(format))) {
    return applySign(
      (value as number).toFixed(parseInt(m[2], 10)),
      m[1] as SignSpec | undefined,
      value,
    );
  }
  if ((m = PERCENT_DECIMALS.exec(format))) {
    const d = parseInt(m[2], 10);
    return applySign(
      new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(value),
      m[1] as SignSpec | undefined,
      value,
    );
  }

  return String(value);
}

// Compact variant of `formatValue`: same style (currency / percent / plain)
// but rendered with `notation: "compact"` so the result reads as "1.2M",
// "$3.4K", "12%". Used as the abbreviation fallback when a KPI value is
// too wide to fit its tile even after font-shrinking — see
// InsightKpi.vue's auto-fit logic. Strings, nulls, and unknown formats
// fall through unchanged so the caller behaves the same as `formatValue`.
export function formatValueCompact(
  value: number | string | bigint | any,
  format?: string,
): string {
  // 1. Catch native BigInts and Arrow Int64 proxy objects
  if (typeof value === "bigint" || (typeof value === "object" && value !== null)) {
    value = Number(value);
  }

  // 2. Catch stringified numbers (ONLY if a format is explicitly requested)
  if (typeof value === "string" && format) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && value.trim() !== "") {
      value = parsed;
    }
  }

  if (typeof value === "string") return value;
  if (value === null || value === undefined || Number.isNaN(value)) return "—";

  const compact = (extra: Intl.NumberFormatOptions = {}): string =>
    new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
      ...extra,
    }).format(value as number);

  if (!format) return compact();

  let m: RegExpExecArray | null;

  if ((m = EXACT_CURRENCY.exec(format))) {
    return applySign(compact({ style: "currency", currency: "USD" }), m[1] as SignSpec | undefined, value as number);
  }
  if ((m = EXACT_PERCENT.exec(format))) {
    return applySign(compact({ style: "percent" }), m[1] as SignSpec | undefined, value as number);
  }
  if ((m = EXACT_COMMA.exec(format))) {
    return applySign(compact(), m[1] as SignSpec | undefined, value as number);
  }

  if ((m = CURRENCY_DECIMALS.exec(format))) {
    return applySign(compact({ style: "currency", currency: "USD" }), m[1] as SignSpec | undefined, value as number);
  }
  if ((m = COMMA_DECIMALS.exec(format))) {
    return applySign(compact(), m[1] as SignSpec | undefined, value as number);
  }
  if ((m = FIXED_DECIMALS.exec(format))) {
    return applySign(compact(), m[1] as SignSpec | undefined, value as number);
  }
  if ((m = PERCENT_DECIMALS.exec(format))) {
    return applySign(compact({ style: "percent" }), m[1] as SignSpec | undefined, value as number);
  }

  return compact();
}
